import { ClockIcon } from '@heroicons/react/24/outline'
import { usePermissions } from 'hooks/usePermissions'
import { useSelector } from 'react-redux'
import { CopyableText } from 'stories/components'
import { formatDate, openAuditLog } from 'utils'

interface AdditionalInfoItem {
  title: string
  value: string
}

interface InputProps {
  loanNumber: any
  program: string
  byteNumber: number
  entityTitle: string
  borrowerName: string
  propertyAddress: string
  mers?: string
  closingDate?: string
  config?: Record<string, any>
  additionalInfo?: AdditionalInfoItem[]
}

export function LoanDetail({
  loanNumber,
  program,
  byteNumber,
  entityTitle,
  borrowerName,
  propertyAddress,
  mers,
  closingDate,
  config,
  additionalInfo = [],
}: InputProps) {
  const [profile, loan] = useSelector((state: any) => [state.auth.profile, state.loan])
  const accountType = profile.isBorrower ? 'borrower' : profile.accountType

  const { hasPermission } = usePermissions()
  const seeLog = hasPermission('CAN_VIEW_AUDIT_LOG')

  const isNewLoan = loanNumber === 'New'

  const showHistory = (table: string, field: string, keys: any, map?: any) => {
    if (isNewLoan) return
    const options = {
      table,
      field,
      keys,
      map,
    }
    openAuditLog(options)
  }

  const sizeClass = 'w-4 h-4'

  // Default order if not configured
  const defaultOrder = [
    'loanNumber',
    'entityName',
    'borrowerName',
    'propertyAddress',
    'loanProgram',
    'mers',
    'closingDate',
  ]
  const fieldsOrder =
    config?.overviewFieldsOrder && Array.isArray(config.overviewFieldsOrder) && config.overviewFieldsOrder.length > 0
      ? config.overviewFieldsOrder
      : defaultOrder

  const fieldRenderers: Record<string, () => JSX.Element | null> = {
    loanNumber: () => {
      if (!config || config.loanNumber?.[accountType] || profile.type) {
        return (
          <div className="flex mb-1">
            <div className="w-36">Loan Number:</div>
            <div className="font-bold ml-2">
              <CopyableText>{byteNumber || loanNumber}</CopyableText>
            </div>
          </div>
        )
      }
      return null
    },
    entityName: () => {
      if (!config || config.entityName?.[accountType] || profile.type) {
        return (
          <div className="flex mb-1">
            <div className="w-36">Entity Name:</div>
            <div className="flex items-center ml-2 group">
              <CopyableText>{entityTitle}</CopyableText>
              {seeLog && (
                <span className="text-gray-500 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover-shadow1 rounded p-1">
                  <ClockIcon
                    className={sizeClass}
                    onClick={() =>
                      showHistory('Borrower', 'entityTitle', { field: 'entityTitle', borrowerSeperator: 'borrower' })
                    }
                  />
                </span>
              )}
            </div>
          </div>
        )
      }
      return null
    },
    borrowerName: () => {
      if (!config || config.borrowerName?.[accountType] || profile.type) {
        return (
          <div className="flex mb-1">
            <div className="w-36">Borrower Name:</div>
            <div className="flex items-center ml-2 group">
              <CopyableText>{borrowerName}</CopyableText>
              {seeLog && (
                <span className="text-gray-500 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover-shadow1 rounded p-1">
                  <ClockIcon
                    className={sizeClass}
                    onClick={() =>
                      showHistory('Borrower', 'firstName', { field: 'firstName', borrowerSeperator: 'borrower' })
                    }
                  />
                </span>
              )}
            </div>
          </div>
        )
      }
      return null
    },
    propertyAddress: () => {
      if (!config || config.propertyAddress?.[accountType] || profile.type) {
        return (
          <div className="flex mb-1">
            <div className="w-36">Property Address:</div>
            <div className="flex items-center ml-2 group">
              <CopyableText copyText={`${propertyAddress}${loan?.isMultipleProperties ? ', ET al' : ''}`}>
                {`${propertyAddress}${loan?.isMultipleProperties ? ', ET al' : ''}`}
              </CopyableText>

              {seeLog && (
                <span className="text-gray-500 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover-shadow1 rounded p-1">
                  <ClockIcon
                    className={sizeClass}
                    onClick={() => showHistory('Loan', 'subjectPropertyAddress', { field: 'subjectPropertyAddress' })}
                  />
                </span>
              )}
            </div>
          </div>
        )
      }
      return null
    },
    loanProgram: () => {
      if (!config || config.loanProgram?.[accountType] || profile.type) {
        return (
          <div className="flex mb-1">
            <div className="w-36">Loan Program:</div>
            <div className="flex items-center ml-2 group">
              <CopyableText>{program}</CopyableText>
            </div>
          </div>
        )
      }
      return null
    },
    mers: () => {
      if (!config || config.mers?.[accountType] || profile.type) {
        return (
          <div className="flex mb-1">
            <div className="w-36">MERS:</div>
            <div className="flex items-center ml-2 group">
              <CopyableText>{mers || ''}</CopyableText>
            </div>
          </div>
        )
      }
      return null
    },
    closingDate: () => {
      if (!config || config.closingDate?.[accountType] || profile.type) {
        return (
          <div className="flex mb-1">
            <div className="w-36">Closing Date:</div>
            <div className="flex items-center ml-2 group">
              <CopyableText>{closingDate ? formatDate(closingDate) : ''}</CopyableText>
            </div>
          </div>
        )
      }
      return null
    },
  }

  return (
    <div className="text-[14px] text-left">
      {fieldsOrder.map((fieldKey: string) => {
        const renderer = fieldRenderers[fieldKey]
        return renderer ? <div key={fieldKey}>{renderer()}</div> : null
      })}
      {additionalInfo.map((item, index) => (
        <div key={`additional-${index}`} className="flex mb-1">
          <div className="w-36">{item.title}:</div>
          <div className="flex items-center ml-2">
            <CopyableText>{item.value}</CopyableText>
          </div>
        </div>
      ))}
    </div>
  )
}
